// Which /bootcamp button a click was, named. The one place the mapping from a
// clicked link to the `bootcamp_cta_clicked` event's `cta` property lives, so
// the page's listener (app/(marketing)/bootcamp/_components/
// bootcamp-click-tracking.tsx) carries no rules of its own and the eval can
// exercise every branch. Pure: no browser, no PostHog.

export type BootcampCtaClick = {
  /** A stable name for the button: `group_checkout`, `private_checkout`,
   *  `filming_checkout`, `see_dates`, `see_pricing`, `apply`,
   *  `check_private_dates`, or `other` for any further button-styled link. */
  cta: string
  /** The checkout plan the link carries, when it is a checkout link. */
  plan?: string
  /** The Friday of the weekend a "Take this weekend" link carries. */
  weekend?: string
  /** The link's own destination, path and query only. */
  href: string
}

/** Class fragments that mark a link as a button rather than prose. */
const BUTTON_CLASS = /(?:^|[\s_-])(?:cta|btn|act|ghost|check)(?:$|[\s_-])/

const CHECKOUT_CTA: Record<string, string> = {
  "coaching-bootcamp-group": "group_checkout",
  "coaching-bootcamp-private": "private_checkout",
  "dfy-filming-addon": "filming_checkout",
}

/**
 * The click a link stands for, or null when it is not a call to action (an
 * inline link in the FAQ, a same-page jump to somewhere else, an external
 * link). `href` is the anchor's attribute exactly as written; `className` its
 * class string, used only to tell an unnamed button from prose.
 */
export function bootcampCtaFor(href: string, className = ""): BootcampCtaClick | null {
  const raw = href.trim()
  if (!raw) return null
  if (raw.startsWith("#")) {
    if (raw === "#dates") return { cta: "see_dates", href: raw }
    if (raw === "#pricing") return { cta: "see_pricing", href: raw }
    return null
  }
  if (!raw.startsWith("/") || raw.startsWith("//")) return null

  const url = new URL(raw, "https://site.invalid")
  const pathAndQuery = `${url.pathname}${url.search}`

  if (url.pathname === "/pay/embedded") {
    const plan = url.searchParams.get("plan") ?? undefined
    const weekend = url.searchParams.get("weekend") ?? undefined
    return {
      cta: (plan && CHECKOUT_CTA[plan]) || "checkout",
      ...(plan ? { plan } : {}),
      ...(weekend ? { weekend } : {}),
      href: pathAndQuery,
    }
  }
  if (url.pathname === "/apply") return { cta: "apply", href: pathAndQuery }
  if (url.pathname === "/contact") return { cta: "check_private_dates", href: pathAndQuery }
  if (BUTTON_CLASS.test(className)) return { cta: "other", href: pathAndQuery }
  return null
}
